import { playSample } from './audio'
import { FRAME_SECONDS, TIMER_TICK_SECONDS } from './constants'
import { GOAL_SUCCESS_SFX } from './missionSfx'
import { snapshotAndConceal } from './primitives/serverStorageTemplate'
import { findStateByName, spawnProgrammedClone } from './sharedHelpers'
import { BehaviorHandler, HudStyle, MissionBehaviors } from './types'

/**
 * RJS05（おちるブロックでサバイバル）のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJS05.rbxl）のギミックを移植する:
 * - ServerScriptService/BlockScript: プレイ開始時に ServerStorage の
 *   ①ブロック を 10×10 = 100 枚クローンし、y=40 の高さへ
 *   (-50 + 10i, 40, -50 + 10j) の格子状に敷き詰める。クローンには学習者が
 *   ①ブロック に付けたプログラムを起動する（Roblox の Clone() が配下の
 *   Script ごと複製されるのと同じ。なきごえですり抜ける等はそのプログラムが
 *   担う）。
 * - ②スタートちてん/SpawnScript: CanCollide が false に変わったら（一度
 *   だけ）StartEvent → 開始メッセージを消し、タイマーを開始する
 *   （EndLocalScript: 毎秒 +1。学習者が ②スタートちてん をすり抜けさせて
 *   プレイヤーをブロックの上へ落とすのが開始の合図）。
 * - Baseplate/BaseScript: プレイヤーが地面（Baseplate）に落ちたら（一度
 *   だけ）Winner 音 + TouchEvent → タイマーを止めて
 *   「きろくは ◯びょう！」を表示する（EndLocalScript / EndScreenGui）。
 * - StarterGui/StartScreenGui: プレイ開始時から
 *   「なきごえでブロックがすりぬけるよ！」の緑の帯を表示する。
 *
 * プレビューでの近似・意図的な差異:
 * - SpawnScript の Changed イベントは CanCollide の毎フレーム監視で代替する。
 * - StartScreenGui / EndScreenGui は HUD テキストで再現する（HUD は 1 枠の
 *   ため、記録表示の後に StartEvent が来ても記録を消さない）。
 * - Winner 音（実機の音源はアセット取得不可）はプロダクト提供の成功音で鳴らす。
 * - タイマーは実機どおり開始直後に 1 になる（Lua: timer += 1 → wait(1) の
 *   順）。開始前に落ちた場合の記録は 0 びょう（実機と同じ）。
 *
 * 未対応:
 * - ③いぬ の Bark/Wow 等の鳴き声: 学習者のサウンドブロックの対象
 *   （ミッション定義 sound: true）であり、環境ギミックではない。
 */

/** ServerStorage のブロックテンプレートのオブジェクト名。 */
const BLOCK_TEMPLATE_NAME = '①ブロック'

/** スタート地点（CanCollide 監視対象）のオブジェクト名。 */
const SPAWN_NAME = '②スタートちてん'

/** 地面（落下＝ゲーム終了の判定）のオブジェクト名。 */
const BASEPLATE_NAME = 'Baseplate'

/** ブロック格子の枚数・間隔・原点・高さ（Lua: -50 + 10 * i, 40, -50 + 10 * j）。 */
const GRID_COUNT = 10
const GRID_STEP = 10
const GRID_ORIGIN = -50
const GRID_HEIGHT_Y = 40

/** 開始メッセージ（StartScreenGui/TextLabel の実測 Text）。 */
const START_TEXT = 'なきごえでブロックがすりぬけるよ！'

/** 開始メッセージのデザイン（StartScreenGui/TextLabel の実測値）。 */
const START_HUD_STYLE: HudStyle = {
  background: '#00AA00',
  color: '#FFFFFF',
  wide: true,
}

/** 記録表示のデザイン（EndScreenGui/TextLabel の実測値）。 */
const END_HUD_STYLE: HudStyle = {
  background: '#550000',
  color: '#FFFFFF',
  wide: true,
}

/** RJS05 の環境ビヘイビアを生成する（プレイ開始毎に呼ばれる）。 */
export const createRjs05Behaviors = (): MissionBehaviors => {
  // Lua: EndLocalScript の timer（生存秒数）。
  let timerSeconds = 0
  // Lua: SpawnScript の changed フラグ（StartEvent は一度だけ）。
  let isStarted = false
  // Lua: BaseScript の isTouch + EndLocalScript の survive（終了は一度だけ）。
  let isEnded = false

  /**
   * ブロック格子の生成（Lua: ServerScriptService/BlockScript）。
   * 実機の ServerStorage 同様、テンプレート本体はプレイ中隠し、クローンに
   * だけ学習者のプログラムを起動する。
   */
  const spawnBlockGrid: BehaviorHandler = (ctx) => {
    const { world } = ctx
    // クローンの複製元はこの時点のテンプレート状態（実機の ServerStorage に
    // 保存された状態に相当）。
    const snapshot = snapshotAndConceal(world, [BLOCK_TEMPLATE_NAME]).get(
      BLOCK_TEMPLATE_NAME,
    )
    if (!snapshot) {
      return
    }
    for (let i = 1; i <= GRID_COUNT; i += 1) {
      for (let j = 1; j <= GRID_COUNT; j += 1) {
        spawnProgrammedClone(ctx, snapshot, 'rjs05_block', {
          x: GRID_ORIGIN + GRID_STEP * i,
          y: GRID_HEIGHT_Y,
          z: GRID_ORIGIN + GRID_STEP * j,
        })
      }
    }
  }

  /**
   * スタート監視 + 生存タイマー（SpawnScript + StartLocalScript +
   * EndLocalScript の統合）。②スタートちてん の CanCollide が false に
   * なったら開始メッセージを消してタイマーを開始する。
   */
  const watchStartAndRunTimer: BehaviorHandler = async (ctx) => {
    const { world } = ctx
    // 実機は StartScreenGui が最初から有効（Enabled=true）。
    ctx.setHudText?.(START_TEXT, START_HUD_STYLE)
    const spawn = findStateByName(world, SPAWN_NAME)
    if (!spawn) {
      return
    }
    // Lua: spawnLocation.Changed を CanCollide の毎フレーム監視で代替する。
    while (!ctx.signal.aborted) {
      const state = world.getState().get(spawn.id)
      if (!state) {
        return
      }
      if (!state.canCollide) {
        isStarted = true
        break
      }
      await ctx.wait(FRAME_SECONDS)
    }
    if (ctx.signal.aborted || !isStarted) {
      return
    }
    // StartLocalScript: 開始メッセージを消す。ただし先に落ちて記録が出て
    // いる場合は消さない（実機も StartEvent は StartScreenGui しか消さない）。
    if (!isEnded) {
      ctx.setHudText?.(null)
    }
    // EndLocalScript: while survive do timer += 1; wait(1) end
    while (!ctx.signal.aborted && !isEnded) {
      timerSeconds += 1
      await ctx.wait(TIMER_TICK_SECONDS)
    }
  }

  /**
   * 地面への落下（Lua: Baseplate/BaseScript + EndLocalScript の touch）。
   * 一度だけ Winner 音を鳴らし、タイマーを止めて記録を表示する。
   */
  const onBaseplateTouched: BehaviorHandler = (ctx) => {
    if (isEnded) {
      return
    }
    isEnded = true
    playSample(GOAL_SUCCESS_SFX.url, GOAL_SUCCESS_SFX.volume, ctx.signal)
    // Lua: "きろくは " .. timer .. "びょう！"
    ctx.setHudText?.(`きろくは ${timerSeconds}びょう！`, END_HUD_STYLE)
  }

  return {
    onPlay: [spawnBlockGrid, watchStartAndRunTimer],
    onTouchCharacter: new Map<string, BehaviorHandler>([
      [BASEPLATE_NAME, onBaseplateTouched],
    ]),
    // ①ブロック は実機では ServerStorage（Script 非実行）。本体では
    // プログラムを起動せず、クローンにだけ startObjectPrograms で起動する。
    serverStorageTemplates: [BLOCK_TEMPLATE_NAME],
  }
}
